import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FirebaseTSFirestore, Where } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { AudioService } from './audio.service';

@Injectable({
  providedIn: 'root'
})
export class UnreadMessagesService {
  private firestore = new FirebaseTSFirestore();
  private auth = new FirebaseTSAuth();
  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();
  private totalUnreadCount = 0;
  private chatUnreadCounts = new Map<string, number>();
  private previousCount = 0;
  private initialized = false;

  constructor(private audioService: AudioService) {
    this.auth.listenToSignInStateChanges(
      user => {
        if (user) {
          console.log('Utilisateur connecté, ID:', user.uid);
          if (!this.initialized) {
            this.initialized = true;
            this.initializeUnreadCount(user.uid);
          }
        } else {
          console.log('Aucun utilisateur connecté');
          this.initialized = false;
          this.unreadCountSubject.next(0);
          this.chatUnreadCounts.clear();
          this.totalUnreadCount = 0;
          this.previousCount = 0;
        }
      }
    );
  }

  private initializeUnreadCount(userId: string) {
    console.log('Initialisation du compteur pour userId:', userId);

    // D'abord, obtenir tous les chats de l'utilisateur
    this.firestore.getCollection({
      path: ["Chats"],
      where: [
        new Where("participants", "array-contains", userId)
      ],
      onComplete: (result) => {
        console.log('Nombre de chats trouvés:', result.docs.length);
        
        // Réinitialiser les compteurs
        this.chatUnreadCounts.clear();
        this.totalUnreadCount = 0;
        
        // Pour chaque chat, écouter sa collection de messages
        result.docs.forEach(chatDoc => {
          const chatId = chatDoc.id;
          console.log('Écoute des messages pour le chat:', chatId);

          // Obtenir d'abord le compte initial des messages non lus
          this.firestore.getCollection({
            path: ["Chats", chatId, "messages"],
            where: [new Where("read", "==", false)],
            onComplete: (initialMessages) => {
              let chatUnreadCount = 0;
              initialMessages.docs.forEach(doc => {
                const message = doc.data();
                if (message.senderId !== userId && message.read === false) {
                  chatUnreadCount++;
                }
              });
              
              // Mettre à jour le compteur initial pour ce chat
              this.chatUnreadCounts.set(chatId, chatUnreadCount);
              this.updateTotalCount();
            }
          });

          // Écouter la sous-collection messages de ce chat pour les mises à jour
          this.firestore.listenToCollection({
            name: chatId + "messages",
            path: ["Chats", chatId, "messages"],
            where: [new Where("read", "==", false)],
            onUpdate: (messagesResult) => {
              let chatUnreadCount = 0;

              messagesResult.docs.forEach(doc => {
                const message = doc.data();
                if (message.senderId !== userId && message.read === false) {
                  chatUnreadCount++;
                }
              });

              // Mettre à jour le compteur pour ce chat
              this.chatUnreadCounts.set(chatId, chatUnreadCount);
              this.updateTotalCount();
            }
          });
        });
      }
    });
  }

  private updateTotalCount() {
    const newTotalCount = Array.from(this.chatUnreadCounts.values()).reduce((sum, count) => sum + count, 0);
    
    // Vérifier s'il y a de nouveaux messages
    if (newTotalCount > this.previousCount) {
      console.log('Nouveau message reçu ! Lecture du son...');
      this.audioService.playMessageNotification();
    }
    
    this.previousCount = newTotalCount;
    this.totalUnreadCount = newTotalCount;
    console.log(`Total des messages non lus: ${this.totalUnreadCount}`);
    this.unreadCountSubject.next(this.totalUnreadCount);
  }

  getUnreadCount() {
    return this.unreadCount$;
  }

  requestSoundPermission() {
    this.audioService.requestPermission();
  }
}
